import * as Sentry from '@sentry/react'
import {
  resolveSentryEnvironment,
  scrubSentryEvent,
} from '@order-up/analytics/sentry'
import { installChunkReloadRecovery } from '@order-up/client/vite'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { AnalyticsBoundary } from './components/AnalyticsBoundary'
import './index.css'
import App from './App.tsx'

// Bridge the build-time Vite env (define'd as the bare `__VITE_ENV__` token in
// vite.config.ts) onto globalThis so `@order-up/common`'s environment detection
// (which reads `globalThis.__VITE_ENV__`) resolves the correct production
// service URLs in a Vite build instead of falling through to the protected
// `.dev` defaults. See packages/common/src/utils/environment.ts.
if (typeof __VITE_ENV__ !== 'undefined') {
  ;(globalThis as Record<string, unknown>).__VITE_ENV__ ??= __VITE_ENV__
}

// Boot recovery: auto-reload once if a JS module / modulepreload fetch fails on
// first load, instead of wedging on a blank/partial page. Registered before
// render so it catches a failed entry/vendor chunk.
installChunkReloadRecovery({
  onRecover: ({ url, reason }) => {
    Sentry.addBreadcrumb({
      category: 'boot',
      level: 'warning',
      message: 'chunk-load recovery reload',
      data: { url, reason },
    })
    Sentry.captureMessage('chunk-load recovery reload', 'warning')
  },
})

// Sentry browser bootstrap (Phase 12 / Initiative #3 PR 8). Mirrors the
// `sendDefaultPii: false` + `beforeSend` posture of the Next.js apps.
// No-op when `VITE_SENTRY_DSN` is unset (dev / CI).
// Resolved once and shared by `environment` and `beforeSend` — they disagreed
// before (one read VITE_APP_ENV first, the other VITE_VERCEL_ENV), so an event
// could be labelled `production` by one and judged non-production by the other.
const sentryEnvironment = resolveSentryEnvironment(
  import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
  import.meta.env.VITE_APP_ENV as string | undefined,
  import.meta.env.VITE_VERCEL_ENV as string | undefined,
  import.meta.env.MODE,
)

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (dsn) {
  Sentry.init({
    dsn,
    environment: sentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    sampleRate: parseFloat(
      (import.meta.env.VITE_SENTRY_SAMPLE_RATE as string | undefined) || '1.0',
    ),
    tracesSampleRate: parseFloat(
      (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined)
        || '0.1',
    ),
    initialScope: {
      tags: { app: 'menu' },
    },
    sendDefaultPii: false,
    beforeSend: (event) => scrubSentryEvent(event, sentryEnvironment),
  })
} else if (import.meta.env.PROD) {
  // Loud breadcrumb: a production build with no DSN means client-side errors
  // — including a white-screen crash like the 2026-05-30 duplicate-React
  // incident — are NOT captured. Set VITE_SENTRY_DSN in the menu Vercel
  // project so the error monitoring + alerting below actually has a feed.
  console.warn(
    '[menu] VITE_SENTRY_DSN is not set — client error monitoring is DISABLED for this build.',
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      Root error boundary ABOVE AnalyticsBoundary so a crash in the very first
      provider (which is exactly where the duplicate-React null-dispatcher blew
      up) renders a graceful error card instead of a blank page, AND reports to
      Sentry (when a DSN is configured) via the global hub init'd above.
      Defense-in-depth — the resolve.dedupe + pnpm.overrides fix prevents the
      crash itself; this makes any future first-paint crash visible + reported
      rather than a silent white screen.
    */}
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-zinc-950">
          <div className="text-center space-y-6 max-w-md px-6">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              <svg
                className="w-10 h-10 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-white">
                Something went wrong
              </h2>
              <p className="text-zinc-400">
                We hit an unexpected error while loading the menu. Please try
                again — if it keeps happening, contact support.
              </p>
            </div>
            <button
              onClick={() => {
                resetError()
                window.location.reload()
              }}
              className="inline-flex items-center justify-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Reload
            </button>
          </div>
        </div>
      )}
    >
      <AnalyticsBoundary>
        <App />
      </AnalyticsBoundary>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
